import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSmsCodeFlow } from '../../../../hooks/useSmsCodeFlow';
import {
  X,
  ShieldAlert,
  MessageSquareLock,
  FileText,
  Loader2,
  ArrowLeft,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { sendPhoneReleaseCode, verifyPhoneRelease } from '../../../../api/register';
import type { RegisterFormData } from '../../../../stores/registerStore';
import type { MatchedEntity } from '../../../../stores/registerStore';

/**
 * 「非本人信息」处理弹窗（认领确认页二级弹窗）
 *
 * 触发：CustomerBindingModal 中用户判定系统档案非本人信息。
 * 设计原则（系统内身份唯一）：命中档案不允许绕过，必须通过以下方式之一处理：
 *  A. 手机号验证申诉 —— 注册用户持有命中号码时，短信验证后仅持有一次性凭证
 *     （不动库）；注册提交时由后端统一腾位/停用：
 *     - 档案主手机号被验证为他人持有 → 停用档案 + 清号 + 通知关联注册用户
 *     - 仅备用电话失效 → 只清备用字段（档案保留）
 *     验证通过后由父级引导用户直接进入密码步完成注册。
 *  B. 提交人工审核 —— 复用人工审核申请（管理员后台裁决）。
 *
 * 申诉选项显示条件：手机号/备用电话命中 且 姓名非精确相同
 * （同名同姓无法自动判定，仅提供人工审核）。
 */
export function MismatchResolutionModal({
  isOpen,
  customer,
  formData,
  onBack,
  onManualReview,
  onResolved,
  onNotify
}: {
  isOpen: boolean;
  customer: MatchedEntity;
  formData: RegisterFormData;
  /** 返回认领确认弹窗 */
  onBack: () => void;
  /** 转人工审核（关闭本弹窗，由父级打开人工申请弹窗） */
  onManualReview: () => void;
  /** 申诉验证通过（关闭本弹窗，携带凭证由父级引导进入密码步） */
  onResolved: (disputeToken: string) => void;
  /** 轻提示（复用注册弹窗 toast） */
  onNotify: (message: string, type?: 'info' | 'success' | 'error') => void;
}) {
  const [mode, setMode] = useState<'choose' | 'verify'>('choose');
  // 命中号码选项：主手机号 / 备用电话（仅列出命中的，未填不参与）
  const phoneHit = customer.matchedFields?.some((m) => m.field === 'phone') ?? false;
  const backupHit = customer.matchedFields?.some((m) => m.field === 'backupPhone') ?? false;
  const nameExactHit =
    customer.matchedFields?.some((m) => m.field === 'fullName' && m.matchWay === 'exact') ?? false;
  const canAppeal = (phoneHit || backupHit) && !nameExactHit;

  const [verifyTarget, setVerifyTarget] = useState<'phone' | 'backupPhone'>(
    phoneHit ? 'phone' : 'backupPhone'
  );
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [error, setError] = useState<string | null>(null);
  // 发送锁 + 60s 重发冷却（公共 Hook）
  const { countdown, sending, send: sendReleaseCode } = useSmsCodeFlow();

  if (!isOpen) return null;

  const appealPhone = verifyTarget === 'phone' ? formData.phone : formData.backupPhone;

  // 发送验证码：验证「注册用户持有该号码」
  const handleSendCode = async () => {
    if (!appealPhone) return;
    setError(null);
    try {
      await sendReleaseCode(() =>
        sendPhoneReleaseCode({
          phone: appealPhone,
          fullName: formData.fullName,
          idCard: formData.idCard || undefined
        })
      );
      setCode('');
      onNotify('验证码已发送，请查收短信', 'success');
    } catch (e) {
      setError(e instanceof Error ? e.message : '验证码发送失败，请稍后重试');
    }
  };

  // 验码通过 → 仅持有一次性凭证（不动库），注册提交时由后端统一腾位/停用
  const handleVerifyAndResolve = async () => {
    if (!appealPhone || !code || verifying) return;
    setError(null);
    setVerifying(true);
    try {
      const { releaseToken } = await verifyPhoneRelease({
        phone: appealPhone,
        code,
        fullName: formData.fullName,
        idCard: formData.idCard || undefined
      });
      onNotify('验证通过，提交注册后原档案将被停用并释放该联系方式', 'success');
      onResolved(releaseToken);
    } catch (e) {
      setError(e instanceof Error ? e.message : '验证失败，请核对验证码后重试');
    } finally {
      setVerifying(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4 max-h-[90vh] overflow-y-auto">
        {/* 标题区 */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-md shadow-amber-500/20">
              <ShieldAlert className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-800">信息不匹配处理</h3>
              <p className="text-sm text-slate-500">系统内身份唯一，请选择合适的处理方式</p>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              title="返回认领确认"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {mode === 'choose' ? (
          <div className="p-6 space-y-4">
            {/* 出入说明：命中 vs 不匹配字段 */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                您判定系统档案「<span className="font-semibold text-amber-900">{customer.fullName}</span>」非本人信息。
                为避免系统内一人多重身份，请通过以下方式处理；处理完成后即可继续注册。
              </p>
            </div>

            {/* A. 手机号验证申诉 */}
            {canAppeal ? (
              <button
                type="button"
                onClick={() => setMode('verify')}
                className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-teal-300 hover:bg-teal-50/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                    <MessageSquareLock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">
                      手机号验证申诉
                      {phoneHit && <span className="ml-2 text-xs font-normal text-teal-600">命中号码：{formData.phone}</span>}
                      {!phoneHit && backupHit && (
                        <span className="ml-2 text-xs font-normal text-teal-600">命中号码：{formData.backupPhone}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {phoneHit && backupHit
                        ? '您的主手机号与备用电话均命中该档案，可选择其一进行短信验证'
                        : '通过短信验证证明该号码为您本人持有，验证通过后请继续完成注册，提交注册时系统将停用原档案（或清除其备用联系方式）并通知相关用户'}
                    </p>
                  </div>
                  <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-teal-500 transition-colors" />
                </div>
              </button>
            ) : (
              <div className="w-full border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-slate-200 rounded-lg flex items-center justify-center shrink-0">
                    <MessageSquareLock className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-500">手机号验证申诉</div>
                    <p className="text-xs text-slate-400 mt-1">
                      {nameExactHit
                        ? '档案姓名与您填写的姓名完全相同（同名同姓场景），系统无法自动判定，请提交人工审核'
                        : '您填写的联系方式未命中该档案，请提交人工审核处理'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* B. 提交人工审核 */}
            <button
              type="button"
              onClick={onManualReview}
              className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-teal-300 hover:bg-teal-50/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">提交人工审核</div>
                  <p className="text-xs text-slate-500 mt-1">
                    填写您的真实信息并提交，由平台客服人工核实档案归属（可上传身份证照片辅助核对）
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-teal-500 transition-colors" />
              </div>
            </button>

            {/* 返回认领确认 */}
            <button
              type="button"
              onClick={onBack}
              className="w-full py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              返回重新核对
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* 验证号码选择（主/备均命中时可切换） */}
            {phoneHit && backupHit && (
              <div>
                <p className="text-sm text-slate-600 mb-2">选择要验证的号码</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setVerifyTarget('phone')}
                    className={`flex-1 py-2.5 px-3 rounded-lg border text-sm transition-colors ${
                      verifyTarget === 'phone'
                        ? 'border-teal-500 bg-teal-50 text-teal-700 font-medium'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    主手机号
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerifyTarget('backupPhone')}
                    className={`flex-1 py-2.5 px-3 rounded-lg border text-sm transition-colors ${
                      verifyTarget === 'backupPhone'
                        ? 'border-teal-500 bg-teal-50 text-teal-700 font-medium'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    备用电话
                  </button>
                </div>
              </div>
            )}

            {/* 持有权确认提示 */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                验证码将发送至 <span className="font-semibold">{appealPhone}</span>，
                请确认该号码由您本人持有并可接收短信；若无法接收，请返回改用人工审核。
              </p>
            </div>

            {/* 验证码输入 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">短信验证码</label>
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 位数字验证码"
                  inputMode="numeric"
                  className="flex-1 py-2.5 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={sending || countdown > 0}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-300 transition-colors whitespace-nowrap"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : countdown > 0 ? (
                    `${countdown}s 后重发`
                  ) : (
                    '发送验证码'
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* 处置动作说明 */}
            <div className="text-xs text-slate-500 space-y-1">
              <p className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-teal-500" />
                提交注册时：原档案的主手机号失效将被停用，仅备用电话失效则只清除该备用电话
              </p>
              <p className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-teal-500" />
                系统将同步通知添加该客户档案的注册用户及时修正信息
              </p>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setMode('choose');
                  setError(null);
                }}
                className="flex-1 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                上一步
              </button>
              <button
                type="button"
                onClick={handleVerifyAndResolve}
                disabled={!code || code.length < 6 || verifying}
                className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-medium rounded-lg transition-colors shadow-sm shadow-teal-600/30 flex items-center justify-center gap-2"
              >
                {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
                {verifying ? '验证中…' : '验证并继续'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}